/** @jsx jsx */
import React, { memo } from 'react';

import { css, styled, jsx } from '@compiled/react';
import type { EntryPointProps } from 'react-relay';
import { Card as SmartCard, TitleBlock, ElementName, CardAction } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { SmartCardProviderMigration } from '@atlassian/jira-linking-platform-provider-migration/src/index.tsx';
import type { SingleLineSmartLinkProps } from './types.tsx';

const smartLinkStyles = css({
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-selectors
	'& > div:nth-child(2)': {
		flex: '0 1 auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		a: {
			wordBreak: 'break-all',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-selectors
	'& > div:nth-child(2) ~ div': {
		flex: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-selectors
	'& > div:last-child': {
		minWidth: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > div': {
		alignSelf: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-selectors
	'& > span + div:nth-of-type(2) span': {
		textAlign: 'left',
	},
});

export const SingleLineSmartLink = memo(
	({
		props: { url, anchorTarget, text, fieldType = 'url' },
	}: EntryPointProps<{}, {}, SingleLineSmartLinkProps, {}>) => (
		<SmartCardProviderMigration>
			<LinkContainer>
				<SmartCard
					appearance="inline"
					url={url}
					ui={{
						hidePadding: true,
						clickableContainer: true,
					}}
					frameStyle="hide"
					showHoverPreview
					actionOptions={{
						hide: false,
						exclude: [CardAction.ChangeStatusAction],
					}}
					analyticsContext={{
						attributes: {
							fieldType,
						},
					}}
					onClick={(event) => event.stopPropagation()}
				>
					<TitleBlock
						hideTitleTooltip
						maxLines={1}
						metadata={[
							{
								name: ElementName.State,
							},
						]}
						css={smartLinkStyles}
						anchorTarget={anchorTarget ?? '_self'}
						text={text}
					/>
				</SmartCard>
			</LinkContainer>
		</SmartCardProviderMigration>
	),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LinkContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& div[data-smart-link-container="true"]': {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "8px" and fallback "4px" do not match and can't be replaced automatically.
		borderRadius: token('border.radius.200', '4px'),
		whiteSpace: 'normal',
		margin: 0,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& div[data-smart-block]': {
		gap: token('space.050'),
	},
});
